import * as React from 'react';
import { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import useAnnouncements, {
    Announcement,
    AnnouncementType,
} from '@/src/Announcements/hooks/useAnnouncements';

/*
 * Platform announcements — the release notes / notices surfaced in the
 * classic dashboard. Straight CRUD on /announcements in the settings
 * card grammar.
 */

const FIELD_LABEL =
    'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

const EMPTY_DRAFT = {
    title: '',
    message: '',
    type: AnnouncementType.Release,
    by: '',
};

export default function AdminAnnouncementsV2() {
    const {
        listAnnouncements,
        createAnnouncement,
        updateAnnouncement,
        deleteAnnouncement,
    } = useAnnouncements();

    const [rows, setRows] = useState<Announcement[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dialog, setDialog] = useState<
        { kind: 'create' } | { kind: 'edit'; row: Announcement } | null
    >(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [busy, setBusy] = useState(false);
    const [dialogError, setDialogError] = useState<string | null>(null);
    const dialogRef = useDialogRef(dialog != null, () => setDialog(null));

    const load = async () => {
        const data = (await listAnnouncements().catch(() => [])) ?? [];
        setRows(
            [...data].sort(
                (a, b) =>
                    moment(b.createdAt).valueOf() -
                    moment(a.createdAt).valueOf(),
            ),
        );
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreate = () => {
        setDraft(EMPTY_DRAFT);
        setDialogError(null);
        setDialog({ kind: 'create' });
    };

    const openEdit = (row: Announcement) => {
        setDraft({
            title: row.title ?? '',
            message: row.message ?? '',
            type: row.type ?? AnnouncementType.Release,
            by: row.by ?? '',
        });
        setDialogError(null);
        setDialog({ kind: 'edit', row });
    };

    const submit = async () => {
        if (!dialog || busy) return;
        if (!draft.title.trim() || !draft.message.trim()) {
            setDialogError('Title and message are required.');
            return;
        }
        setBusy(true);
        setDialogError(null);
        const body = {
            title: draft.title.trim(),
            message: draft.message.trim(),
            type: draft.type,
            by: draft.by.trim(),
        };
        const ok =
            dialog.kind === 'create'
                ? await createAnnouncement(body).catch(() => null)
                : await updateAnnouncement(dialog.row.id, body).catch(
                      () => null,
                  );
        setBusy(false);
        if (ok) {
            setDialog(null);
            toast.success(
                dialog.kind === 'create' ? 'Announcement posted' : 'Saved',
            );
            load();
        } else {
            setDialogError("Couldn't save — try again.");
        }
    };

    const remove = async (row: Announcement) => {
        if (!window.confirm(`Delete "${row.title}"?`)) return;
        const prev = rows;
        setRows((current) => current.filter((r) => r.id !== row.id));
        const ok = await deleteAnnouncement(row.id)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setRows(prev);
            toast.error("Couldn't delete the announcement.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Announcements"
                desc="Release notes and notices shown to every dashboard user."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {rows.length} posted
                        </span>
                        <button
                            type="button"
                            onClick={openCreate}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            New announcement
                        </button>
                    </>
                }
            >
                {rows.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        Nothing posted yet.
                    </p>
                )}
                {rows.map((row) => (
                    <div
                        key={row.id}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="min-w-0 flex-1 truncate font-semibold">
                            {row.title}
                        </span>
                        <span
                            className={
                                'rounded-full border px-2 py-[2px] font-mono text-[9px] font-semibold tracking-[0.1em] ' +
                                (row.type === AnnouncementType.Release
                                    ? 'border-brand-soft bg-brand-soft text-brand-ink'
                                    : 'border-border text-muted-foreground')
                            }
                        >
                            {row.type === AnnouncementType.Release
                                ? 'RELEASE'
                                : 'NOTICE'}
                        </span>
                        {row.by && (
                            <span className="hidden max-w-[120px] truncate text-muted-foreground sm:block">
                                {row.by}
                            </span>
                        )}
                        <span className="whitespace-nowrap text-[11.5px] text-faint">
                            {moment(row.createdAt).format('MMM D')}
                        </span>
                        <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            <button
                                type="button"
                                onClick={() => openEdit(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                edit
                            </button>
                            <button
                                type="button"
                                title="Delete announcement"
                                onClick={() => remove(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
            </SettingsCard>

            {dialog && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setDialog(null)}
                >
                    <div
                        ref={dialogRef}
                        {...dialogPanelProps(
                            dialog.kind === 'create'
                                ? 'New announcement'
                                : 'Edit announcement',
                        )}
                        className="w-full max-w-[440px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            {dialog.kind === 'create'
                                ? 'New announcement'
                                : 'Edit announcement'}
                        </div>
                        <label className={FIELD_LABEL}>Title</label>
                        <input
                            autoFocus
                            className="ps-input mb-3 w-full text-[13px]"
                            value={draft.title}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    title: e.target.value,
                                }))
                            }
                        />
                        <label className={FIELD_LABEL}>Message</label>
                        <textarea
                            className="ps-input mb-3 h-[120px] w-full resize-y text-[13px] leading-relaxed"
                            value={draft.message}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    message: e.target.value,
                                }))
                            }
                        />
                        <div className="mb-4 grid grid-cols-2 gap-3">
                            <div>
                                <label className={FIELD_LABEL}>Type</label>
                                <select
                                    className="ps-input w-full cursor-pointer appearance-none text-[13px]"
                                    value={draft.type}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            type: +e.target
                                                .value as AnnouncementType,
                                        }))
                                    }
                                >
                                    <option
                                        value={AnnouncementType.Release}
                                    >
                                        Release
                                    </option>
                                    <option
                                        value={
                                            AnnouncementType.Announcement
                                        }
                                    >
                                        Notice
                                    </option>
                                </select>
                            </div>
                            <div>
                                <label className={FIELD_LABEL}>By</label>
                                <input
                                    className="ps-input w-full text-[13px]"
                                    placeholder="Your name"
                                    value={draft.by}
                                    onChange={(e) =>
                                        setDraft((d) => ({
                                            ...d,
                                            by: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={submit}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {busy
                                    ? 'Saving…'
                                    : dialog.kind === 'create'
                                    ? 'Post announcement'
                                    : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setDialog(null)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {dialogError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {dialogError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
